import { useMemo } from 'react';
import { stringifyVariables } from 'urql';

import { FeatureFlag } from '@revelio/core';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import { useFilterContext } from '../../filters/contexts/FilterProvider';
import type { PrimaryFilterId } from '../../filters/types/filters';
import {
  getTreemapDrilldownLevelOrder,
  isTreemapDrilldownSegment,
} from '../../filters/utils/treemapDrilldownLevels';
import { nestedTreemapLookupKey } from '../Chart/utils/buildNestedTreemapData';
import { TOP_N_LIMIT } from '../DataProvider/constants';
import type { LevelDimension, TableDimension } from '../DataProvider/types';
import { useStableReference } from '../DataProvider/utils/use-stable-reference';
import type { MetricValues } from '../PageConfigProvider/types/metric';
import { isCompositionV4BreakdownDimension } from './composition-v4-breakdowns';
import { getCompositionV4TableSource } from './composition-v4-tables';
import { dimensionFromPrimaryFilter } from './convert-composition-v4-data';
import type { PlannedQuery } from './plan-data-queries';
import type { ResolvedData } from './resolve-items';
import {
  getBreakdownLevelPair,
  taxonomyOf,
  taxonomyOfLevel,
} from './taxonomy-levels';
import {
  type CompositionV4Drilldown,
  useCompositionV4Tables,
} from './use-composition-v4-tables';
import { useTopCompaniesByEntity } from './use-top-companies';

type DrilledInto = { level: string; id: string | number };

type CategoryView = {
  tables: PlannedQuery['tables'];
  drilldown: CompositionV4Drilldown;
};

type CompaniesView = { companiesWithin: CompositionV4Drilldown };

type DrilldownView = CategoryView | CompaniesView;

const DRILLDOWN_TABLE_KEY = 'drilldown';

export const findLevelOfLadderId = (
  dimension: TableDimension,
  id: string,
  geographySelectionListIds: readonly string[]
): LevelDimension | undefined =>
  taxonomyOf(dimension, geographySelectionListIds)?.ladder.find(
    (step) => step.id === id
  )?.level;

export const getDrilldownBreakdownLevel = (
  level: LevelDimension,
  geographySelectionListIds: readonly string[]
): LevelDimension | undefined => {
  const ladder =
    taxonomyOf(taxonomyOfLevel(level), geographySelectionListIds)?.ladder ?? [];
  const index = ladder.findIndex((step) => step.level === level);
  return index === -1 ? undefined : ladder[index + 1]?.level;
};

const drillsIntoCompanies = (
  dimension: TableDimension,
  levelId: string,
  geographySelectionListIds: readonly string[]
): boolean => {
  if (!isTreemapDrilldownSegment(dimension)) return false;
  const order = getTreemapDrilldownLevelOrder(
    dimension,
    geographySelectionListIds
  );
  const index = order.indexOf(levelId);
  return index !== -1 && order[index + 1] === 'company';
};

const getDrilldownView = ({
  primaryDimension,
  dimension,
  drilledInto,
  metrics,
  sublevel,
  geographySelectionListIds,
}: {
  primaryDimension: PrimaryFilterId;
  dimension: TableDimension;
  drilledInto: DrilledInto;
  metrics: readonly MetricValues[];
  sublevel: boolean;
  geographySelectionListIds: readonly string[];
}): DrilldownView | undefined => {
  const level = findLevelOfLadderId(
    dimension,
    drilledInto.level,
    geographySelectionListIds
  );
  const id = Number(nestedTreemapLookupKey(drilledInto.id));
  if (!level || !Number.isFinite(id)) return undefined;
  const breakdownLevel = getDrilldownBreakdownLevel(
    level,
    geographySelectionListIds
  );
  if (!breakdownLevel) {
    return drillsIntoCompanies(
      dimension,
      drilledInto.level,
      geographySelectionListIds
    ) &&
      metrics.includes('headcount') &&
      isCompositionV4BreakdownDimension(
        dimensionFromPrimaryFilter(primaryDimension)
      )
      ? { companiesWithin: { level, id } }
      : undefined;
  }

  const levels = sublevel
    ? getBreakdownLevelPair(
        dimension,
        breakdownLevel,
        geographySelectionListIds
      )
    : [breakdownLevel];
  if (!levels) return undefined;

  const table = {
    key: DRILLDOWN_TABLE_KEY,
    dimensions: [dimensionFromPrimaryFilter(primaryDimension), ...levels],
    metrics: [...new Set(metrics)],
  };
  return getCompositionV4TableSource(primaryDimension, table)
    ? { drilldown: { level, id }, tables: { [table.key]: table } }
    : undefined;
};

const areDrilldownViewsEqual = (
  previous: DrilldownView | undefined,
  next: DrilldownView | undefined
): boolean =>
  previous === next ||
  stringifyVariables(previous) === stringifyVariables(next);

const NO_TABLES: PlannedQuery['tables'] = {};

export const useCompositionV4Drilldown = ({
  primaryDimension,
  dimension,
  drilledInto,
  metrics,
  sublevel = false,
}: {
  primaryDimension: PrimaryFilterId;
  dimension: TableDimension;
  drilledInto: DrilledInto | undefined;
  metrics: readonly MetricValues[];
  sublevel?: boolean;
}): ResolvedData | undefined => {
  const isCompositionV4Enabled = useUnleashFlag(FeatureFlag.CompositionV4);
  const { geographySelectionListIds } = useFilterContext();
  const view = useStableReference(
    isCompositionV4Enabled && drilledInto
      ? getDrilldownView({
          primaryDimension,
          dimension,
          drilledInto,
          metrics,
          sublevel,
          geographySelectionListIds,
        })
      : undefined,
    areDrilldownViewsEqual
  );
  const categoryView = view && 'tables' in view ? view : undefined;
  const companiesWithin =
    view && 'companiesWithin' in view ? view.companiesWithin : undefined;

  const categoryResult = useCompositionV4Tables({
    primaryDimension,
    tables: categoryView?.tables ?? NO_TABLES,
    drilldown: categoryView?.drilldown,
    pause: categoryView === undefined,
  });
  const companiesResult = useTopCompaniesByEntity({
    primaryDimension,
    within: companiesWithin,
    limit: TOP_N_LIMIT,
    pause: companiesWithin === undefined,
  });

  return useMemo(() => {
    if (companiesWithin) return companiesResult;
    return categoryView && categoryResult
      ? { ...categoryResult, data: categoryResult.data?.[DRILLDOWN_TABLE_KEY] }
      : undefined;
  }, [categoryView, categoryResult, companiesWithin, companiesResult]);
};
